import { CtaJoinMembershipWrapper_ActivityFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { activityPath } from "@outschool/routes";
import { gql } from "@outschool/ui-apollo";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession,
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React, { useCallback } from "react";

import { useLoggedOutMembershipsExperiment } from "../Subscriptions";

type CtaJoinMembershipWrapperProps = {
  activity: CtaJoinMembershipWrapper_ActivityFragment;
  controlComponent: React.ReactElement;
  size: string;
  isMobile: boolean;
  loggedInJoinCtaTrackingName?: string;
  loggedOutDesktopJoinCtaTrackingName?: string;
  loggedOutMobileJoinCtaTrackingName?: string;
  loggedInCta?: string;
};

export default function CtaJoinMembershipWrapper({
  activity,
  controlComponent,
  size,
  isMobile,
  loggedInJoinCtaTrackingName,
  loggedOutDesktopJoinCtaTrackingName,
  loggedOutMobileJoinCtaTrackingName,
  loggedInCta,
}: CtaJoinMembershipWrapperProps) {
  const { enterLoginFlow } = useLoginFlowContext();
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\CtaJoinMembershipWrapper"
  );
  const navigate = useNavigation();
  const { isLoggedIn, currentUser } = useSession();
  const { requiresMembershipForEnrollment } =
    useLoggedOutMembershipsExperiment();

  const onClickJoinToViewPlans = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.append("joinMembershipModal", "true");
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      navigate(newUrl, { replace: true });
    },
    [navigate]
  );

  if (
    isLoggedIn &&
    currentUser?.requiresMembershipForEnrollment &&
    !currentUser?.hasSubscription
  ) {
    return (
      <TrackedButton
        variant="contained"
        onClick={onClickJoinToViewPlans}
        sx={{ width: "100%" }}
        trackingName={loggedInJoinCtaTrackingName}
        size={size}
      >
        {loggedInCta ? loggedInCta : t`View Plans`}
      </TrackedButton>
    );
  }

  if (
    isLoggedIn &&
    currentUser?.requiresMembershipForEnrollment &&
    currentUser.hasSubscription &&
    !currentUser?.canPayWithSubscriptionCredits
  ) {
    return (
      <TrackedButton
        variant="contained"
        onClick={onClickJoinToViewPlans}
        sx={{ width: "100%" }}
        trackingName={loggedInJoinCtaTrackingName}
        size={size}
      >
        {t`Resume to Enroll`}
      </TrackedButton>
    );
  }

  if (!isLoggedIn && requiresMembershipForEnrollment) {
    return (
      <TrackedButton
        variant="contained"
        onClick={() => {
          enterLoginFlow({
            authTrigger: AuthTrigger.JOIN_OUTSCHOOL,
            newUrl: activityPath(activity) + "?joinMembershipModal=true",
          });
        }}
        sx={{ width: "100%" }}
        trackingName={
          isMobile
            ? loggedOutMobileJoinCtaTrackingName
            : loggedOutDesktopJoinCtaTrackingName
        }
        trackingUniqueId={activity.uid}
        size={size}
      >
        {t`Join to View Plans`}
      </TrackedButton>
    );
  }
  return controlComponent;
}

CtaJoinMembershipWrapper.fragments = {
  activity: gql`
    fragment CtaJoinMembershipWrapper_activity on Activity {
      uid
      slug_id
      title
      isClub
    }
  `,
};
